/** Natural title height shared only by cards on the same visible native grid row. */
export function groupVisualRows(items, tolerance = 1) {
  const rows = [];
  for (const item of [...items].sort((a, b) => a.top - b.top || a.left - b.left)) {
    let row = rows.at(-1);
    if (!row || Math.abs(item.top - row.top) > tolerance) {
      row = { top: item.top, items: [], height: 0 };
      rows.push(row);
    }
    row.items.push(item);
    row.height = Math.max(row.height, item.height);
  }
  return rows;
}

const property = '--havehad-card-row-title-height';

class GridTitleRows {
  #grid;
  #resize;
  #mutation;
  #titles = new Set();
  #frame = 0;

  constructor(grid) {
    this.#grid = grid;
    this.#resize = new ResizeObserver(this.schedule);
    this.#resize.observe(grid);
    this.#mutation = new MutationObserver(this.schedule);
    this.#mutation.observe(grid, {
      subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['class', 'hidden', 'product-grid-view'],
    });
    this.schedule();
  }

  schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#align();
    });
  };

  #align() {
    const current = new Set();
    const measurements = [];
    for (const item of this.#grid.children) {
      if (!item.matches('.product-grid__item')) continue;
      const title = item.querySelector('product-card .hv-product-title-row');
      const name = title?.querySelector('.hv-product-title-name');
      if (!title || !name) continue;
      current.add(title);
      if (!this.#titles.has(title)) this.#resize.observe(name);
      const box = item.getBoundingClientRect();
      const text = name.getBoundingClientRect();
      const chip = title.querySelector('.hv-product-color-chip')?.getBoundingClientRect();
      // The child text's natural height does not inherit the parent's row minimum.
      // Reading it avoids reset/measure/write cycles and ResizeObserver feedback.
      if (box.width > 0 && text.height > 0 && name.getClientRects().length) {
        measurements.push({ top: box.top, left: box.left, height: Math.max(text.height, chip?.height || 0), title });
      } else {
        title.style.removeProperty(property);
      }
    }
    for (const title of this.#titles) {
      if (current.has(title)) continue;
      const name = title.querySelector('.hv-product-title-name');
      if (name) this.#resize.unobserve(name);
      title.style.removeProperty(property);
    }
    this.#titles = current;
    for (const row of groupVisualRows(measurements)) {
      const height = `${row.height}px`;
      for (const { title } of row.items) {
        if (title.style.getPropertyValue(property) !== height) title.style.setProperty(property, height);
      }
    }
  }

  disconnect() {
    cancelAnimationFrame(this.#frame);
    this.#resize.disconnect();
    this.#mutation.disconnect();
    for (const title of this.#titles) title.style.removeProperty(property);
    this.#titles.clear();
  }
}

if (typeof document !== 'undefined') {
  const grids = new Map();
  const sync = () => {
    for (const [grid, controller] of grids) {
      if (!grid.isConnected) { controller.disconnect(); grids.delete(grid); }
    }
    for (const grid of document.querySelectorAll('.product-grid')) {
      if (!grids.has(grid)) grids.set(grid, new GridTitleRows(grid));
    }
  };
  const scheduleAll = () => { sync(); grids.forEach((controller) => controller.schedule()); };
  const lifecycle = new MutationObserver((records) => {
    const relevant = records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) =>
      node instanceof Element && (node.matches('.product-grid') || node.querySelector('.product-grid'))
    ));
    if (relevant) sync();
  });
  lifecycle.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('resize', scheduleAll, { passive: true });
  window.addEventListener('pageshow', scheduleAll);
  document.fonts?.addEventListener('loadingdone', scheduleAll);
  document.fonts?.ready.then(scheduleAll);
  sync();
}
